import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Package } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { useApp, type Container, type Sale } from "@/context/AppContext";

export const Route = createFileRoute("/tools/reports")({
  component: ReportsPage,
});

type Period = "all" | "month" | "year";
interface Totals {
  units: number;
  revenue: number;
}

const PERIODS: { value: Period; label: string }[] = [
  { value: "all", label: "All time" },
  { value: "year", label: "This year" },
  { value: "month", label: "This month" },
];

function inPeriod(saleDate: string, period: Period): boolean {
  if (period === "all") return true;
  const d = new Date(saleDate);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  if (period === "year") return d.getFullYear() === now.getFullYear();
  return (
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
  );
}

const money = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD" });

/**
 * Sales by location (TNK-193 v1). Pure client-side: group the cached sales by
 * their snapshot `containerId`, roll the cached container tree up (each node =
 * own + descendants), and render an expandable tree of units + gross revenue.
 * Cancelled sales are excluded; product/bundle + unattributed sales land in a
 * "Mixed / no location" bucket. Best-effort attribution until placement-aware
 * picking (TNK-192) makes multi-bin exact.
 */
function ReportsPage() {
  const { sales, containers } = useApp();
  const [period, setPeriod] = useState<Period>("all");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const byId = useMemo(
    () => new Map(containers.map((c) => [c.id, c])),
    [containers],
  );

  const counted = useMemo(
    () =>
      sales.filter((s: Sale) => !s.cancelledAt && inPeriod(s.saleDate, period)),
    [sales, period],
  );

  // Own (non-rolled-up) totals per container, plus the unattributed bucket
  // (no containerId, or a containerId whose container is gone from the tree).
  const { own, unattributed } = useMemo(() => {
    const own = new Map<string, Totals>();
    const unattributed: Totals = { units: 0, revenue: 0 };
    for (const s of counted) {
      const id = s.containerId;
      if (id && byId.has(id)) {
        const t = own.get(id) ?? { units: 0, revenue: 0 };
        t.units += 1;
        t.revenue += s.salePrice;
        own.set(id, t);
      } else {
        unattributed.units += 1;
        unattributed.revenue += s.salePrice;
      }
    }
    return { own, unattributed };
  }, [counted, byId]);

  const childrenByParent = useMemo(() => {
    const m = new Map<string | null, Container[]>();
    for (const c of containers) {
      const key = c.parentId && byId.has(c.parentId) ? c.parentId : null;
      const arr = m.get(key) ?? [];
      arr.push(c);
      m.set(key, arr);
    }
    for (const arr of m.values())
      arr.sort((a, b) => a.name.localeCompare(b.name));
    return m;
  }, [containers, byId]);

  // Subtree totals (own + all descendants), memoized via post-order DFS.
  const subtree = useMemo(() => {
    const acc = new Map<string, Totals>();
    const visit = (id: string): Totals => {
      const seeded = acc.get(id);
      if (seeded) return seeded;
      const t: Totals = { ...(own.get(id) ?? { units: 0, revenue: 0 }) };
      for (const child of childrenByParent.get(id) ?? []) {
        const ct = visit(child.id);
        t.units += ct.units;
        t.revenue += ct.revenue;
      }
      acc.set(id, t);
      return t;
    };
    for (const c of containers) visit(c.id);
    return acc;
  }, [containers, childrenByParent, own]);

  const grand = useMemo(() => {
    let units = unattributed.units;
    let revenue = unattributed.revenue;
    for (const c of childrenByParent.get(null) ?? []) {
      const t = subtree.get(c.id);
      if (t) {
        units += t.units;
        revenue += t.revenue;
      }
    }
    return { units, revenue };
  }, [childrenByParent, subtree, unattributed]);

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Render a node (and its children) only when its subtree has sales — keeps
  // empty branches out while still surfacing ancestors of nodes that do.
  const renderNode = (node: Container, depth: number): React.ReactNode => {
    const t = subtree.get(node.id);
    if (!t || t.units === 0) return null;
    const kids = (childrenByParent.get(node.id) ?? []).filter((c) => {
      const ct = subtree.get(c.id);
      return ct && ct.units > 0;
    });
    const isOpen = !collapsed.has(node.id);
    return (
      <div key={node.id}>
        <div
          className="flex items-center gap-2 border-b border-border/50 py-1.5 pr-2 text-sm"
          style={{ paddingLeft: `${depth * 16 + 4}px` }}
        >
          {kids.length > 0 ? (
            <button
              type="button"
              onClick={() => toggle(node.id)}
              className="text-muted-foreground hover:text-foreground"
              aria-label={isOpen ? "Collapse" : "Expand"}
            >
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          ) : (
            <span className="inline-block w-4" />
          )}
          <span className="min-w-0 flex-1 truncate text-foreground">
            {node.name}
            {node.archivedAt && (
              <span className="ml-1.5 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-700">
                archived
              </span>
            )}
            <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              {node.kind}
            </span>
          </span>
          <span className="shrink-0 tabular-nums text-muted-foreground">
            {t.units} {t.units === 1 ? "sale" : "sales"}
          </span>
          <span className="w-24 shrink-0 text-right font-semibold tabular-nums text-foreground">
            {money(t.revenue)}
          </span>
        </div>
        {isOpen && kids.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  const roots = (childrenByParent.get(null) ?? []).filter((c) => {
    const t = subtree.get(c.id);
    return t && t.units > 0;
  });

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                period === p.value
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">
          Sales by location
        </h1>
        <p className="text-sm text-muted-foreground">
          Gross sales rolled up the storage tree. Each row totals that container
          and everything inside it. Cancelled sales excluded.
        </p>
      </div>

      {/* Grand total */}
      <div className="flex items-center gap-2 border-b border-border py-2 text-sm font-semibold">
        <span className="min-w-0 flex-1 text-foreground">All locations</span>
        <span className="shrink-0 tabular-nums text-muted-foreground">
          {grand.units} {grand.units === 1 ? "sale" : "sales"}
        </span>
        <span className="w-24 shrink-0 text-right tabular-nums text-foreground">
          {money(grand.revenue)}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {grand.units === 0 ? (
          <div className="rounded-md border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
            No sales in this period.
          </div>
        ) : (
          <>
            {roots.map((c) => renderNode(c, 0))}
            {unattributed.units > 0 && (
              <div className="mt-2 flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                <Package className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 text-foreground">
                  Mixed / no location
                  <span className="ml-1.5 text-xs text-muted-foreground">
                    (bundles + unattributed)
                  </span>
                </span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {unattributed.units}{" "}
                  {unattributed.units === 1 ? "sale" : "sales"}
                </span>
                <span className="w-24 shrink-0 text-right font-semibold tabular-nums text-foreground">
                  {money(unattributed.revenue)}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
